import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Building2, X, Plus, Loader2, AlertTriangle, ArrowLeft } from "lucide-react";
import api from "../lib/api";
import { useStable } from "../context/StableContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";

const secondary = (o) => o.partita_iva || o.codice_fiscale || o.email || o.phone || "";

export default function Organizations() {
  const { current, role, assist } = useStable();
  const navigate = useNavigate();
  const canManage = assist ? false : ["manager", "admin"].includes(role);
  const [all, setAll] = useState(null);
  const [error, setError] = useState(false);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("active");
  const [creating, setCreating] = useState(false);

  const load = () => {
    if (!current?.id) return;
    setAll(null); setError(false);
    api.get(`/stables/${current.id}/organizations/directory`, { params: { archived: tab === "archived" } })
      .then((r) => setAll(r.data || []))
      .catch(() => setError(true));
  };
  useEffect(load, [current?.id, tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    if (!all) return null;
    const s = q.trim().toLowerCase();
    if (!s) return all;
    const norm = s.replace(/\s+/g, "").toUpperCase();
    return all.filter((o) =>
      (o.legal_name || "").toLowerCase().includes(s) ||
      (o.partita_iva || "").includes(norm) ||
      (o.codice_fiscale || "").includes(norm) ||
      (o.email || "").toLowerCase().includes(s) ||
      (o.phone || "").toLowerCase().includes(s)
    );
  }, [all, q]);

  return (
    <div className="max-w-3xl mx-auto px-1 sm:px-0" data-testid="organizations-page">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-[#1B3B2B]">Organizzazioni</h1>
          <p className="text-sm text-[#5F6B64]">Le organizzazioni collegate a {current?.name}.</p>
        </div>
        {canManage && (
          <Button data-testid="org-new-btn" onClick={() => setCreating(true)} className="bg-[#1B3B2B] hover:bg-[#142d21] shrink-0">
            <Plus className="h-4 w-4 sm:mr-2" /> <span className="hidden sm:inline">Nuova organizzazione</span>
          </Button>
        )}
      </div>

      <div className="mb-4 inline-flex rounded-lg border border-[#E5DEC9] bg-white p-0.5" data-testid="org-tabs">
        {[["active", "Attive"], ["archived", "Archiviate"]].map(([k, lbl]) => (
          <button key={k} data-testid={`org-tab-${k}`} onClick={() => setTab(k)}
            className={`px-4 py-1.5 text-sm rounded-md transition-colors ${tab === k ? "bg-[#1B3B2B] text-white" : "text-[#5F6B64] hover:text-[#1B3B2B]"}`}>
            {lbl}
          </button>
        ))}
      </div>

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9AA39C]" />
        <Input data-testid="org-search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Cerca ragione sociale, P.IVA, CF…" className="pl-9" autoFocus={false} />
        {q && (
          <button onClick={() => setQ("")} data-testid="org-search-clear"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9AA39C] hover:text-[#1B3B2B]">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {error ? (
        <div className="text-center py-16 text-[#5F6B64]" data-testid="org-error">Impossibile caricare le organizzazioni. Riprova.</div>
      ) : all === null ? (
        <div className="space-y-2" data-testid="org-loading">
          {[0, 1, 2].map((i) => <div key={i} className="h-16 rounded-xl bg-[#F0EAE1]/50 animate-pulse" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-[#5F6B64]" data-testid="org-empty">
          {all.length === 0 ? (tab === "archived" ? "Nessuna organizzazione archiviata." : "Nessuna organizzazione collegata a questa scuderia.") : "Nessun risultato per la ricerca."}
        </div>
      ) : (
        <div className="space-y-2" data-testid="org-list">
          {filtered.map((o) => (
            <button key={o.id} data-testid={`org-row-${o.id}`} onClick={() => navigate(`/organizzazioni/${o.id}`)}
              className="w-full flex items-center gap-3 rounded-xl border border-[#E5DEC9] bg-white px-3 py-3 text-left hover:border-[#8C532B]/50 transition-colors">
              <span className="h-10 w-10 shrink-0 rounded-lg bg-[#8C532B]/10 text-[#8C532B] flex items-center justify-center">
                <Building2 className="h-5 w-5" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block font-medium text-[#1B3B2B] truncate">{o.legal_name || "Senza nome"}</span>
                <span className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-[#9AA39C]">
                  {o.organization_type && <span className="px-1.5 py-0.5 rounded bg-[#F0EAE1] text-[#5F6B64]">{o.organization_type}</span>}
                  {secondary(o) && <span className="truncate">{secondary(o)}</span>}
                </span>
              </span>
              <Building2 className="h-4 w-4 text-[#C9C2B4] shrink-0" />
            </button>
          ))}
        </div>
      )}

      {creating && (
        <CreateOrgDialog stableId={current.id}
          onClose={() => setCreating(false)}
          onCreated={(id) => { setCreating(false); navigate(`/organizzazioni/${id}`); }}
          onView={(id) => { setCreating(false); navigate(`/organizzazioni/${id}`); }}
          onRestored={() => { setCreating(false); load(); }} />
      )}
    </div>
  );
}

function CreateOrgDialog({ stableId, onClose, onCreated, onView, onRestored }) {
  const [form, setForm] = useState({ legal_name: "", organization_type: "", partita_iva: "", codice_fiscale: "", email: "", phone: "", pec: "" });
  const [candidates, setCandidates] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const doCreate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/stables/${stableId}/organizations`, form);
      if (data?.piva_duplicate_warning) toast.warning("Un'altra organizzazione ha la stessa Partita IVA.");
      toast.success("Organizzazione creata");
      onCreated(data.organization_id || data.id);
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "Serve il ruolo Gestore." : (e?.response?.data?.detail || "Errore"));
    } finally { setBusy(false); }
  };
  const submit = async () => {
    if (!form.legal_name.trim()) { toast.error("Inserisci la ragione sociale"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/stables/${stableId}/organizations/duplicate-check`, {
        legal_name: form.legal_name, partita_iva: form.partita_iva, codice_fiscale: form.codice_fiscale });
      const c = data?.candidates || [];
      if (c.length > 0) { setCandidates(c); setBusy(false); return; }
      await doCreate();
    } catch { await doCreate(); }
  };
  const restore = async (id) => {
    try {
      await api.post(`/stables/${stableId}/organizations/${id}/restore`);
      toast.success("Organizzazione ripristinata");
      onRestored(id);
    } catch (e) { toast.error(e?.response?.status === 403 ? "Serve il ruolo Gestore per ripristinare." : (e?.response?.data?.detail || "Errore")); }
  };

  const fields = [["legal_name", "Ragione sociale *"], ["organization_type", "Tipo (SSD, ASD, Azienda…) (opz.)"],
    ["partita_iva", "Partita IVA (opz.)"], ["codice_fiscale", "Codice fiscale (opz.)"],
    ["email", "Email (opz.)"], ["pec", "PEC (opz.)"], ["phone", "Telefono (opz.)"]];

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="org-create-modal" className="max-h-[85vh] flex flex-col overflow-hidden" onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader><DialogTitle>Nuova organizzazione</DialogTitle></DialogHeader>
        <div className="space-y-3 overflow-auto flex-1 min-h-0 pr-1">
          {candidates && candidates.length > 0 && (
            <OrgDupWarning candidates={candidates} onView={onView} onRestore={restore} />
          )}
          {fields.map(([k, lbl]) => (
            <div key={k} className="space-y-1">
              <Label htmlFor={`oc-${k}`} className="text-xs text-[#5F6B64]">{lbl}</Label>
              <Input id={`oc-${k}`} data-testid={`org-create-field-${k}`} value={form[k]}
                onChange={(e) => set(k, e.target.value)} autoFocus={false} autoComplete="off" />
            </div>
          ))}
        </div>
        <div className="pt-3 shrink-0 flex gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="org-create-cancel">Annulla</Button>
          {candidates && candidates.length > 0 ? (
            <Button className="flex-1 bg-[#1B3B2B] hover:bg-[#142d21]" disabled={busy} data-testid="org-create-anyway" onClick={doCreate}>
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Crea comunque
            </Button>
          ) : (
            <Button className="flex-1 bg-[#1B3B2B] hover:bg-[#142d21]" disabled={busy} data-testid="org-create-submit" onClick={submit}>
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Crea organizzazione
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Shared warning block for organization duplicate detection (archived-aware).
export function OrgDupWarning({ candidates, onView, onRestore }) {
  const hasArchived = candidates.some((c) => c.archived);
  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-2" data-testid="org-dup-warning">
      <div className="flex items-start gap-2 text-sm text-amber-800">
        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
        <span>{hasArchived ? "Esiste già un'organizzazione archiviata con questi dati." : "Potrebbe esistere già un'organizzazione corrispondente."}</span>
      </div>
      <div className="space-y-1.5">
        {candidates.map((c) => (
          <div key={c.id} className="rounded-md bg-white border border-amber-200 px-2.5 py-2">
            <div className="flex items-center justify-between gap-2">
              <span className="min-w-0">
                <span className="block text-sm text-[#1B3B2B] truncate">{c.legal_name || "Senza nome"}{c.archived && <span className="ml-1.5 text-[10px] px-1 py-0.5 rounded border border-[#9AA39C] text-[#5F6B64]">Archiviata</span>}</span>
                <span className="text-[10px] text-[#9AA39C]">{(c.reasons || []).map((r) => r === "partita_iva" ? "stessa P.IVA" : r === "codice_fiscale" ? "stesso CF" : "stessa ragione sociale").join(" · ")}</span>
              </span>
            </div>
            <div className="mt-1.5 flex gap-1.5">
              <Button type="button" size="sm" variant="outline" className="h-7 text-xs" data-testid={`org-dup-view-${c.id}`} onClick={() => onView(c.id)}>Visualizza</Button>
              {c.archived && <Button type="button" size="sm" variant="outline" className="h-7 text-xs" data-testid={`org-dup-restore-${c.id}`} onClick={() => onRestore(c.id)}>Ripristina</Button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
